import { Trash2, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '@/auth/useScope';
import { useAppStore } from '@/app/appStore';
import { usePanelStore } from '@/panels/panelStore';
import { useMobileWorkspaceStore } from '@/panels/mobileWorkspaceStore';
import { type WorkbenchTemplate } from '@/api/workbenchTemplates';
import {
  hydratePanelsFromLayoutConfig,
  LAYOUT_TEMPLATE_SURFACE,
  parseLayoutConfig,
} from './layoutTemplates';
import { getOpsPresets, type OpsPresetTemplate } from './opsPresets';
import { useDeleteTemplate, useTemplatesList } from './useTemplates';

type TemplateListProps = {
  onClose: () => void;
};

/**
 * Minimal list of saved templates. Delete and reopen are scope-gated on the
 * backend; we also hide the delete button unless the user holds
 * workbench:templates:write.
 */
export function TemplateList({ onClose }: TemplateListProps) {
  const { t } = useTranslation('common');
  const canRead = useScope('workbench:templates:read');
  const canWrite = useScope('workbench:templates:write');
  const query = useTemplatesList(undefined, { enabled: canRead });
  const del = useDeleteTemplate();
  const panels = usePanelStore((s) => s.panels);
  const openSurface = usePanelStore((s) => s.openSurface);
  const replaceWorkspacePanels = usePanelStore((s) => s.replaceWorkspacePanels);
  const clearMobileStack = useMobileWorkspaceStore((s) => s.clearStack);
  const pushMobilePanel = useMobileWorkspaceStore((s) => s.pushPanel);
  const setLastAgentAction = useAppStore((s) => s.setLastAgentAction);
  const presets = getOpsPresets();
  const [pendingLayoutId, setPendingLayoutId] = useState<string | null>(null);
  const workspacePanelCount = useMemo(() => panels.filter((panel) => !panel.detached).length, [panels]);
  const layoutItems = (query.data ?? []).filter((tpl) => tpl.surface === LAYOUT_TEMPLATE_SURFACE);
  const items = (query.data ?? []).filter((tpl) => tpl.surface !== LAYOUT_TEMPLATE_SURFACE);

  if (!canRead) {
    return (
      <div className="p-3 text-sm text-muted-foreground">
        {t('templates.empty')}
      </div>
    );
  }

  function openSavedTemplate(tpl: WorkbenchTemplate): void {
    openSurface(tpl.surface, tpl.config, { title: tpl.name });
    setLastAgentAction({
      correlationId: `tpl-${tpl.template_id}`,
      description: t('templates.reopened'),
      timestamp: new Date().toISOString(),
    });
    onClose();
  }

  function openSavedLayout(tpl: WorkbenchTemplate): void {
    const parsed = parseLayoutConfig(tpl.config);
    if (!parsed) {
      setLastAgentAction({
        correlationId: `tpl-${tpl.template_id}`,
        description: t('layouts.invalidConfig'),
        timestamp: new Date().toISOString(),
      });
      setPendingLayoutId(null);
      return;
    }

    const hydrated = hydratePanelsFromLayoutConfig(parsed);
    const droppedSurfaces = [...new Set([
      ...hydrated.droppedUnknownSurfaces,
      ...hydrated.droppedInvalidPayloads,
    ])];

    if (hydrated.panels.length === 0) {
      setLastAgentAction({
        correlationId: `tpl-${tpl.template_id}`,
        description: t('layouts.noValidPanels', {
          defaultValue: 'This layout has no valid panels to restore.',
        }),
        timestamp: new Date().toISOString(),
      });
      setPendingLayoutId(null);
      return;
    }

    replaceWorkspacePanels(hydrated.panels);
    clearMobileStack();
    for (const panel of hydrated.panels) {
      pushMobilePanel(panel.panelId);
    }
    setLastAgentAction({
      correlationId: `tpl-${tpl.template_id}`,
      description:
        droppedSurfaces.length > 0
          ? t('layouts.loadedPartialNamed', {
              defaultValue:
                'Layout "{{name}}" restored {{count}} surfaces. Dropped: {{surfaces}}.',
              name: tpl.name,
              count: hydrated.panels.length,
              surfaces: droppedSurfaces.join(', '),
            })
          : t('layouts.loadedCountNamed', {
              defaultValue: 'Layout "{{name}}" restored {{count}} surfaces.',
              name: tpl.name,
              count: hydrated.panels.length,
            }),
      timestamp: new Date().toISOString(),
    });
    setPendingLayoutId(null);
    onClose();
  }

  function requestSavedLayoutOpen(tpl: WorkbenchTemplate): void {
    if (workspacePanelCount > 0 && pendingLayoutId !== tpl.template_id) {
      setPendingLayoutId(tpl.template_id);
      return;
    }
    openSavedLayout(tpl);
  }

  function openPreset(preset: OpsPresetTemplate): void {
    const parsed = parseLayoutConfig(preset.config);
    if (!parsed) {
      setLastAgentAction({
        correlationId: `preset-${preset.preset_id}`,
        description: t('layouts.invalidConfig'),
        timestamp: new Date().toISOString(),
      });
      return;
    }

    const hydrated = hydratePanelsFromLayoutConfig(parsed);
    const droppedSurfaces = [...new Set([
      ...hydrated.droppedUnknownSurfaces,
      ...hydrated.droppedInvalidPayloads,
    ])];

    if (hydrated.panels.length === 0) {
      setLastAgentAction({
        correlationId: `preset-${preset.preset_id}`,
        description: t('templates.presetNoValidPanels', {
          defaultValue: 'Preset has no valid surfaces to restore.',
        }),
        timestamp: new Date().toISOString(),
      });
      return;
    }

    replaceWorkspacePanels(hydrated.panels);
    clearMobileStack();
    for (const panel of hydrated.panels) {
      pushMobilePanel(panel.panelId);
    }
    setLastAgentAction({
      correlationId: `preset-${preset.preset_id}`,
      description:
        droppedSurfaces.length > 0
          ? t('templates.presetOpenedPartial', {
              defaultValue:
                'Preset restored {{count}} surfaces. Dropped: {{surfaces}}.',
              count: hydrated.panels.length,
              surfaces: droppedSurfaces.join(', '),
            })
          : t('templates.presetOpenedCount', {
              defaultValue: 'Preset restored {{count}} surfaces.',
              count: hydrated.panels.length,
            }),
      timestamp: new Date().toISOString(),
    });
    onClose();
  }

  function presetTileLabel(surface: string): string {
    return surface
      .split('_')
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 3);
  }

  return (
    <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="template-list">
      {query.isLoading ? (
        <div className="p-3 text-sm text-muted-foreground">
          {t('saved', { defaultValue: 'Loading…' })}
        </div>
      ) : null}

      {query.isError ? (
        <div role="alert" className="px-3 pb-2 text-xs text-destructive">
          {t('templates.loadError')}
        </div>
      ) : null}

      <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('layouts.savedSectionTitle', { defaultValue: 'Saved layouts' })}
      </div>

      {layoutItems.length === 0 ? (
        <div className="px-3 pb-2 text-xs text-muted-foreground">
          {t('layouts.empty', { defaultValue: 'No saved layouts yet.' })}
        </div>
      ) : (
        <ul>
          {layoutItems.map((tpl) => (
            <li key={tpl.template_id} className="px-3 py-2 hover:bg-muted">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => requestSavedLayoutOpen(tpl)}
                  className="flex flex-1 items-center gap-2 text-left text-sm focus:outline-none"
                  data-testid={`template-layout-open-${tpl.template_id}`}
                >
                  <Play size={14} className="text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{tpl.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {t('layouts.menuLabel', { defaultValue: 'Workspace layouts' })}
                    </div>
                  </div>
                </button>
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => void del.mutate(tpl.template_id)}
                    disabled={del.isPending}
                    aria-label={t('templates.delete')}
                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              {pendingLayoutId === tpl.template_id ? (
                <div className="mt-2 rounded-md border border-[color:var(--wb-warning)] bg-[color:var(--wb-warning-muted)] p-2 text-xs text-[color:var(--wb-warning)]">
                  <p>
                    {t('templates.presetReplaceConfirm', {
                      defaultValue:
                        'Opening this preset will replace the current workspace panels.',
                    })}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => openSavedLayout(tpl)}
                      className="rounded border border-current px-2 py-1 hover:bg-background/60"
                    >
                      {t('templates.confirmPresetOpen', { defaultValue: 'Replace workspace' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingLayoutId(null)}
                      className="rounded border border-current px-2 py-1 hover:bg-background/60"
                    >
                      {t('templates.cancel')}
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('templates.savedSectionTitle')}
      </div>

      {items.length === 0 ? (
        <div className="px-3 pb-2 text-xs text-muted-foreground">
          {t('templates.empty')}
        </div>
      ) : (
        <ul>
          {items.map((tpl) => (
            <li
              key={tpl.template_id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-muted"
            >
              <button
                type="button"
                onClick={() => openSavedTemplate(tpl)}
                className="flex flex-1 items-center gap-2 text-left text-sm focus:outline-none"
              >
                <Play size={14} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{tpl.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {tpl.surface}
                  </div>
                </div>
              </button>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => void del.mutate(tpl.template_id)}
                  disabled={del.isPending}
                  aria-label={t('templates.delete')}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-1 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('templates.presetsTitle')}
      </div>
      <ul>
        {presets.map((preset) => (
          <li
            key={preset.preset_id}
            className="px-3 py-2"
          >
            <div className="rounded-md border border-border bg-card p-2 transition-colors hover:bg-muted/40">
              <button
                type="button"
                onClick={() => openPreset(preset)}
                className="flex w-full items-start gap-3 text-left focus:outline-none"
                data-testid={`template-preset-${preset.preset_id}`}
              >
                <div className="grid h-16 w-20 shrink-0 grid-cols-12 grid-rows-9 gap-1 rounded border border-border bg-background p-1">
                  {preset.config.tiles.map((tile) => (
                    <div
                      key={tile.id}
                      className="flex min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-sm bg-primary/15 px-1 text-[9px] font-semibold uppercase text-primary"
                      style={{
                        gridColumn: `${tile.grid.x + 1} / span ${tile.grid.w}`,
                        gridRow: `${tile.grid.y + 1} / span ${tile.grid.h}`,
                      }}
                    >
                      {presetTileLabel(tile.surface)}
                    </div>
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Play size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm">{t(preset.name_key)}</span>
                    <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                      {t('templates.presetBadge')}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(preset.description_key)}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t('templates.presetSurfaceCount', {
                      defaultValue: '{{count}} surfaces',
                      count: preset.config.tiles.length,
                    })}
                  </p>
                </div>
              </button>
            </div>
          </li>
        ))}
      </ul>

      {items.length === 0 && !query.isLoading ? (
        <div className="px-3 pb-2 text-xs text-muted-foreground">
          {t('templates.emptyHint')}
        </div>
      ) : null}
    </div>
  );
}
